import { handleResponseErrors } from '@restDataAccess/handleResponseErrors';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { authorizationHeader } from '../authorizationHeader.ts';
import { assertApiBaseForFetch } from './getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from './useGetApiUrl.ts';

type postSingleFetchProps<B> = {
  baseApiRoute: ApiNameType;
  cacheKeys: (string | number | null | undefined)[];
  headers?: HeadersInit;
  returnJsonResult?: boolean;
  route: string;
  body: B;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
};

/**
 * Authenticated read performed via POST (TanStack Query).
 *
 * Mirrors {@link useAuthApiGetSingleFetch} but sends `body` as a JSON POST, for endpoints that
 * model a query as a POST (e.g. the cloud inventory `type/query` endpoint). Caches under
 * `cacheKeys` only — serialize the body into `cacheKeys` so distinct queries cache separately.
 * Callers use this for cross-site cloud reads whose body and response are not scoped to the
 * selected site.
 *
 * @typeParam T - JSON response type.
 * @typeParam B - Request body type.
 */
export const useAuthApiPostSingleFetch = <T, B>({
  baseApiRoute,
  route,
  body,
  cacheKeys,
  headers,
  refetchOnWindowFocus = false,
  enabled = true,
  returnJsonResult,
}: postSingleFetchProps<B>) => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);

  const getData = async (): Promise<T> => {
    try {
      const base = assertApiBaseForFetch(api);
      const response = await fetch(`${base}${route}`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          ...authorizationHeader(auth.user),
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      return await handleResponseErrors<T>(response, 'object', returnJsonResult);
    } catch (error) {
      console.error(`Failed to fetch`, error);
      throw error;
    }
  };

  const queryKey = cacheKeys.filter(
    (key): key is string | number => key !== null && key !== undefined
  );

  return useQuery<T>({
    queryKey,
    queryFn: getData,
    refetchOnWindowFocus,
    enabled: enabled && api != null,
  });
};
